/**
 * The adoption review list: which proposals start ticked, how they are
 * grouped, and when Adopt must ask before declining.
 *
 * On the FOG project every one of 509 proposals started unticked, and a single
 * press of Adopt declined all of them - including correct ones at 100%. The
 * planner now stamps each proposal with `defaultAccept` (its target carries the
 * file's label word for word and that label is unique on the form) and
 * `repeatedOnForm` (the label appears more than once on the form: left and
 * right lead, wound 1 and wound 2). This module turns those into the page's
 * starting state and the confirmation text.
 *
 * Pure - see adoptReviewDefaults.test.ts.
 */

export interface ReviewRowLike {
  rowNumber: number;
  disposition: string;
  defaultAccept?: boolean;
  repeatedOnForm?: boolean;
  /** How the planner paired the row: 'anchored_position' and 'partner' are replacements. */
  matchedBy?: string;
  /** Set on a merge: the twin an earlier adoption added, removed when this row is applied. */
  mergesWith?: string;
}

/**
 * The starting selection. A proposal starts ticked only when the planner said
 * so AND it is not in a repeated block - the second condition is checked here
 * too, so a planner that ever sent both flags true could not tick a proposal
 * that might belong to the other copy.
 */
export function initialAccepted(rows: ReviewRowLike[]): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  for (const r of rows) {
    if (r.disposition === 'review' && r.defaultAccept === true && r.repeatedOnForm !== true) {
      out[r.rowNumber] = true;
    }
  }
  return out;
}

/**
 * The review list's groups, in the order the page shows them (design §6):
 * merges of an earlier run's twins, then replacements found by position
 * between matched neighbours or by a partner field, then the ordinary fuzzy
 * proposals, then those in repeated blocks. File order is kept in each.
 */
export function splitReviewRows<T extends ReviewRowLike>(rows: T[]): {
  merges: T[]; replacements: T[]; repeated: T[]; single: T[];
} {
  const merges: T[] = [];
  const replacements: T[] = [];
  const repeated: T[] = [];
  const single: T[] = [];
  for (const r of rows) {
    if (r.disposition !== 'review') continue;
    if (r.mergesWith) merges.push(r);
    else if (r.matchedBy === 'anchored_position' || r.matchedBy === 'partner') replacements.push(r);
    else (r.repeatedOnForm ? repeated : single).push(r);
  }
  return { merges, replacements, repeated, single };
}

/** How many proposals pressing Adopt now would decline. */
export function declinedCount(rows: ReviewRowLike[], accepted: Record<number, boolean>): number {
  return rows.filter((r) => r.disposition === 'review' && !accepted[r.rowNumber]).length;
}

export function needsDeclineConfirm(declined: number): boolean {
  return declined > 0;
}

export function declineConfirmText(declined: number): string {
  const one = declined === 1;
  return `${declined} proposed ${one ? 'match is' : 'matches are'} not ticked and will be ` +
    `declined: ${one ? 'that question keeps its' : 'those questions keep their'} current ` +
    'name. You can go back and tick them, or adopt now and review them later by uploading ' +
    'the same file again.';
}
